import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useAsync } from './useAsync';
import { apiClient, ACTIVE_ORG_STORAGE_KEY } from '@/core/api';
import type { MeResponse, OrganizationSummary } from '@/types/api';
import { useAuth } from '@/contexts/AuthContext';

function fetchMe() {
  return apiClient<MeResponse>('/users/me');
}

function resolveOrgIdFromMe(me: MeResponse | null): string | null {
  if (!me) return null;
  const fromUser = me.user.organization_id ?? me.user.organization_ids?.[0];
  if (fromUser != null) return String(fromUser);
  const fromOrganizations = me.organizations.find((org) => org.id != null)?.id;
  return fromOrganizations != null ? String(fromOrganizations) : null;
}

/** Reads the active org id from localStorage — stable across renders. */
function readStoredOrgId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
}

export function useMe() {
  const { isAuthenticated } = useAuth();
  const fn = useCallback(() => fetchMe(), []);
  return useAsync<MeResponse>(isAuthenticated ? fn : null);
}

/**
 * Manages the active organization stored in localStorage.
 */
export function useOrganization() {
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(readStoredOrgId);

  const setActiveOrgId = useCallback((id: string | number) => {
    const str = String(id);
    window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, str);
    setActiveOrgIdState(str);
  }, []);

  const clearActiveOrganizationId = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    }
    setActiveOrgIdState(null);
  }, []);

  return { activeOrgId, setActiveOrgId, clearActiveOrganizationId };
}

/**
 * Bootstraps the org context for the app shell.
 *
 * Strategy:
 * 1. If `activeOrgId` is already in localStorage → app is ready once /users/me
 *    confirms the user exists (warm fetch). If `/users/me` returns "User not
 *    registered", we clear stale storage (e.g. prod org id hitting testing Datastore).
 * 2. If no org is stored → fetch `/users/me` to see which orgs the user belongs to.
 *    a. If 1 org → auto-select it, mark ready.
 *    b. If 0 orgs → needsSetup = true → redirect to /setup.
 *    c. If multiple → (future: org picker). For now auto-select first.
 */
export function useOrgBootstrap(): {
  ready: boolean;
  needsSetup: boolean;
  orgs: OrganizationSummary[];
  activeOrgId: string | null;
  setActiveOrgId: (id: string | number) => void;
  loading: boolean;
  error: string | null;
} {
  const { activeOrgId, setActiveOrgId, clearActiveOrganizationId } =
    useOrganization();
  const { isAuthenticated } = useAuth();

  // Keep /users/me warm even when an org id is already stored so UI surfaces
  // (e.g. header profile menu) always have the latest org metadata.
  const shouldFetchMe = isAuthenticated;
  const fn = useCallback(() => fetchMe(), []);
  const { data, loading, error } = useAsync<MeResponse>(shouldFetchMe ? fn : null);

  useLayoutEffect(() => {
    if (!shouldFetchMe || loading || !error?.includes('User not registered')) {
      return;
    }
    // Stale org id from another environment (e.g. prod localStorage hitting
    // testing Datastore): clear so /setup + fresh /users/me can run.
    if (activeOrgId) {
      clearActiveOrganizationId();
    }
  }, [
    activeOrgId,
    clearActiveOrganizationId,
    error,
    loading,
    shouldFetchMe,
  ]);

  const orgs = useMemo(() => data?.organizations ?? [], [data?.organizations]);
  const resolvedOrgId = useMemo(() => resolveOrgIdFromMe(data), [data]);
  const isMissingRegistration =
    error != null && (error.includes('403') || error.includes('User not registered'));

  // Reconcile local org state against /users/me.
  useEffect(() => {
    if (!resolvedOrgId) return;
    if (!activeOrgId) {
      setActiveOrgId(resolvedOrgId);
      return;
    }
    const activeInMembership = orgs.some(
      (org) => org.id != null && String(org.id) === String(activeOrgId),
    );
    if (!activeInMembership && orgs.length > 0) {
      setActiveOrgId(resolvedOrgId);
    }
  }, [activeOrgId, orgs, resolvedOrgId, setActiveOrgId]);

  const clearingStaleOrganization =
    Boolean(activeOrgId) &&
    !loading &&
    Boolean(error?.includes('User not registered'));

  if (loading || clearingStaleOrganization) {
    return {
      ready: false,
      needsSetup: false,
      orgs,
      activeOrgId,
      setActiveOrgId,
      loading: true,
      error: null,
    };
  }

  if (activeOrgId) {
    return {
      ready: true,
      needsSetup: false,
      orgs,
      activeOrgId,
      setActiveOrgId,
      loading: false,
      error: null,
    };
  }

  // We know the org from /users/me and are syncing it into localStorage.
  if (resolvedOrgId) {
    return {
      ready: false,
      needsSetup: false,
      orgs,
      activeOrgId,
      setActiveOrgId,
      loading: true,
      error: null,
    };
  }

  // Still waiting on /users/me
  return {
    ready: false,
    needsSetup: !loading && (isMissingRegistration || (!error && resolvedOrgId == null)),
    orgs,
    activeOrgId,
    setActiveOrgId,
    loading,
    error: isMissingRegistration ? null : error,
  };
}
